/*
 * Pure helpers for the directory console (listings, people, authors).
 *
 * Nothing here knows a vertical: every noun, service and kind comes from the
 * domain's directory.vocab (GET /domain/{id}/directory). The backend validates
 * the same rules; these exist so the form can show errors before a round trip.
 */

export const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
export const DAY_LABELS = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};
export const COLLECTIONS = ['listings', 'people', 'authors'];
export const STATUSES = ['active', 'hidden'];
export const PRICE_RANGES = ['$', '$$', '$$$'];

const SLUG_RE = /^[a-z0-9-]+$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const ATTR_KEY_RE = /^[a-z0-9_-]+$/;

export const isSlug = (s) => typeof s === 'string' && s.length > 0 && s.length <= 200 && SLUG_RE.test(s);

export const slugify = (text) => String(text || '')
  .normalize('NFKD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/'/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

export const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/* ── vocab ─────────────────────────────────────────────────────────────── */

const DEFAULT_NOUNS = {
  listing: 'listing', listings: 'listings', person: 'person', people: 'people',
  service: 'service', services: 'services',
};

/** Nouns with generic fallbacks, so a domain without vocab still reads sensibly. */
export const nounsFrom = (vocab) => ({ ...DEFAULT_NOUNS, ...((vocab && vocab.nouns) || {}) });

/** The title-cased tab label for a collection, e.g. listings -> "Salons". */
export const collectionLabel = (vocab, collection) => {
  if (collection === 'authors') return 'Authors';
  const n = nounsFrom(vocab);
  return capitalize(collection === 'listings' ? n.listings : n.people);
};

export const singularLabel = (vocab, collection) => {
  if (collection === 'authors') return 'Author';
  const n = nounsFrom(vocab);
  return capitalize(collection === 'listings' ? n.listing : n.person);
};

/** A feature flag; absent means off (each vertical opts in). */
export const featureOn = (vocab, key) => !!(vocab && vocab.features && vocab.features[key]);

export const termOptions = (vocab, section) => ((vocab && vocab[section]) || [])
  .filter((t) => t && typeof t.key === 'string')
  .map((t) => ({ key: t.key, label: t.label || t.key }));

export const termLabel = (vocab, section, key) => {
  const hit = termOptions(vocab, section).find((t) => t.key === key);
  return hit ? hit.label : key;
};

/** The public path segment for a collection, from vocab.paths. */
export const publicPath = (vocab, collection, slug) => {
  const paths = (vocab && vocab.paths) || {};
  const seg = collection === 'listings' ? paths.listing : collection === 'people' ? paths.person : 'author';
  return seg ? `/${seg}/${slug}/` : null;
};

/**
 * Parse and check the vocab JSON textarea. Returns { vocab, error }.
 * Mirrors backend normalize_vocab: nouns are strings, paths are slugs and
 * distinct, services/kinds are lists of {key, label} with unique slug keys,
 * features are booleans, copy is strings. Other keys pass through.
 */
export const parseVocab = (text) => {
  const trimmed = String(text || '').trim();
  if (!trimmed) return { vocab: null, error: null };
  let v;
  try {
    v = JSON.parse(trimmed);
  } catch (e) {
    return { vocab: null, error: `Not valid JSON: ${e.message}` };
  }
  const fail = (error) => ({ vocab: null, error });
  if (!v || typeof v !== 'object' || Array.isArray(v)) return fail('vocab must be a JSON object');
  if (v.nouns !== undefined) {
    if (!v.nouns || typeof v.nouns !== 'object') return fail('vocab.nouns must be an object');
    for (const [k, val] of Object.entries(v.nouns)) {
      if (typeof val !== 'string' || !val.trim()) return fail(`vocab.nouns.${k} must be a non-empty string`);
    }
  }
  if (v.paths !== undefined) {
    if (!v.paths || typeof v.paths !== 'object') return fail('vocab.paths must be an object');
    for (const [k, val] of Object.entries(v.paths)) {
      if (!isSlug(val)) return fail(`vocab.paths.${k} must match [a-z0-9-]+`);
    }
    const vals = Object.values(v.paths);
    if (new Set(vals).size !== vals.length) return fail('vocab.paths values must be distinct');
  }
  for (const section of ['services', 'kinds']) {
    if (v[section] === undefined) continue;
    if (!Array.isArray(v[section])) return fail(`vocab.${section} must be a list of {key, label}`);
    const seen = new Set();
    for (let i = 0; i < v[section].length; i += 1) {
      const t = v[section][i];
      if (!t || typeof t !== 'object') return fail(`vocab.${section}[${i}] must be an object`);
      if (!isSlug(t.key)) return fail(`vocab.${section}[${i}].key must match [a-z0-9-]+`);
      if (seen.has(t.key)) return fail(`vocab.${section}: duplicate key "${t.key}"`);
      if (typeof t.label !== 'string' || !t.label.trim()) return fail(`vocab.${section}[${i}].label is required`);
      seen.add(t.key);
    }
  }
  if (v.features !== undefined) {
    if (!v.features || typeof v.features !== 'object'
        || !Object.values(v.features).every((x) => typeof x === 'boolean')) {
      return fail('vocab.features must be an object of true/false');
    }
  }
  if (v.copy !== undefined) {
    if (!v.copy || typeof v.copy !== 'object'
        || !Object.values(v.copy).every((x) => typeof x === 'string')) {
      return fail('vocab.copy must be an object of strings');
    }
  }
  if (v.quick !== undefined) {
    const keys = new Set((v.services || []).map((t) => t.key));
    if (!Array.isArray(v.quick) || v.quick.some((k) => !keys.has(k))) {
      return fail('vocab.quick must list keys from vocab.services');
    }
  }
  return { vocab: v, error: null };
};

/* ── hours ─────────────────────────────────────────────────────────────── */

/**
 * Stored hours ({mon: [["09:00","18:00"]], ...}, missing day = closed) to
 * editor rows: one per day, in week order, each { day, closed, intervals }.
 */
export const hoursToDays = (hours) => DAYS.map((day) => {
  const intervals = (hours && Array.isArray(hours[day]) ? hours[day] : [])
    .filter((iv) => Array.isArray(iv) && iv.length === 2)
    .map(([open, close]) => ({ open, close }));
  return { day, closed: intervals.length === 0, intervals: intervals.length ? intervals : [{ open: '09:00', close: '17:00' }] };
});

/** A sensible starting week for a listing that had no hours. */
export const defaultDays = () => DAYS.map((day) => ({
  day, closed: day === 'sun', intervals: [{ open: '09:00', close: '18:00' }],
}));

/**
 * Editor rows back to the stored map. Closed days are omitted. Returns
 * { hours, errors } where errors maps day -> message; hours is null when
 * every day is closed (nothing to show), matching the backend.
 */
export const daysToHours = (days) => {
  const hours = {};
  const errors = {};
  (days || []).forEach(({ day, closed, intervals }) => {
    if (closed) return;
    const clean = (intervals || []).map(({ open, close }) => [open, close]);
    for (const [open, close] of clean) {
      if (!TIME_RE.test(open || '') || !TIME_RE.test(close || '')) {
        errors[day] = 'Use HH:MM times';
        return;
      }
      if (open >= close) {
        errors[day] = `${open} must be before ${close}`;
        return;
      }
    }
    clean.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
    for (let i = 1; i < clean.length; i += 1) {
      if (clean[i][0] < clean[i - 1][1]) {
        errors[day] = 'Intervals overlap';
        return;
      }
    }
    if (clean.length) hours[day] = clean;
  });
  return { hours: Object.keys(hours).length ? hours : null, errors };
};

/** One-line summary for tables and read-only views, e.g. "Mon–Fri 09:00–18:00 · Sat 10:00–16:00". */
export const summarizeHours = (hours) => {
  if (!hours) return 'Not listed';
  const parts = [];
  let run = null;
  const fmt = (day) => (hours[day] || []).map(([o, c]) => `${o}–${c}`).join(', ');
  DAYS.forEach((day) => {
    const text = fmt(day);
    if (run && run.text === text) {
      run.end = day;
    } else {
      if (run) parts.push(run);
      run = { start: day, end: day, text };
    }
  });
  if (run) parts.push(run);
  return parts
    .filter((r) => r.text)
    .map((r) => `${DAY_LABELS[r.start]}${r.end !== r.start ? `–${DAY_LABELS[r.end]}` : ''} ${r.text}`)
    .join(' · ') || 'Closed';
};

/* ── attributes ────────────────────────────────────────────────────────── */

const fmtValue = (v) => (v === null || v === undefined ? '' : String(v));

export const attributesToRows = (attrs) => Object.entries(attrs || {}).map(([key, value]) => ({ key, value: fmtValue(value) }));

/** "true"/"false" become booleans, plain numbers become numbers, anything else stays text. */
export const parseAttributeValue = (raw) => {
  const s = String(raw ?? '').trim();
  if (s === 'true') return true;
  if (s === 'false') return false;
  if (s !== '' && /^-?\d+(\.\d+)?$/.test(s)) return Number(s);
  return s;
};

/** Rows back to the stored map. Blank rows are dropped. Returns { attributes, error }. */
export const rowsToAttributes = (rows) => {
  const out = {};
  for (const { key, value } of rows || []) {
    const k = String(key || '').trim();
    if (!k && String(value || '').trim() === '') continue;
    if (!ATTR_KEY_RE.test(k) || k.length > 64) return { attributes: null, error: `Attribute key "${k}" must use a-z, 0-9, _ or -` };
    if (Object.prototype.hasOwnProperty.call(out, k)) return { attributes: null, error: `Attribute "${k}" appears twice` };
    out[k] = parseAttributeValue(value);
  }
  return { attributes: out, error: null };
};

/* ── payloads ──────────────────────────────────────────────────────────── */

const EDITABLE = {
  listings: ['name', 'kind', 'services', 'street', 'city', 'state', 'zip', 'county', 'city_slug', 'lat', 'lon',
    'phone', 'website', 'instagram', 'booking_url', 'description', 'hours', 'photos', 'price_range',
    'claimed', 'featured', 'attributes', 'status'],
  people: ['name', 'title', 'bio', 'photo', 'specialties', 'listing_slugs', 'instagram', 'booking_url', 'website',
    'license_number', 'portfolio', 'consent_at', 'status'],
  authors: ['name', 'title', 'bio', 'photo', 'credentials', 'instagram', 'website', 'person_slug', 'status'],
};

export const editableFields = (collection) => EDITABLE[collection] || [];

const toNumberOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : v;
};

/**
 * The request body for a create (with slug) or an update. Only fields the API
 * accepts are sent (it rejects unknown ones), and a field the stored document
 * never had and the form left empty is not sent, so a save does not add a
 * pile of nulls to an ingested listing.
 */
export const buildPayload = (collection, draft, { original = {}, isNew = false } = {}) => {
  const body = {};
  editableFields(collection).forEach((f) => {
    if (!(f in draft)) return;
    let v = draft[f];
    if (f === 'lat' || f === 'lon') v = toNumberOrNull(v);
    if (typeof v === 'string') v = v.trim();
    const emptyNow = v === '' || v === null || v === undefined || (Array.isArray(v) && v.length === 0)
      || (f === 'attributes' && v && Object.keys(v).length === 0);
    const hadBefore = original[f] !== undefined && original[f] !== null && original[f] !== ''
      && !(Array.isArray(original[f]) && original[f].length === 0);
    if (emptyNow && !hadBefore && !isNew) return;
    if (emptyNow && isNew && !['claimed', 'featured'].includes(f)) return;
    body[f] = v === '' ? null : v;
  });
  if (isNew) body.slug = draft.slug;
  return body;
};

/* ── media ─────────────────────────────────────────────────────────────── */

const SITE_BUCKET = 'https://storage.googleapis.com/default-websites-load-balancer-bucket/images/';

/** Public URL of a directory media name (what the sites serve at /images/<name>). */
export const mediaUrl = (name) => (name ? SITE_BUCKET + encodeURIComponent(name) : null);

export const moveItem = (list, from, to) => {
  const next = [...list];
  if (to < 0 || to >= next.length) return next;
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};
